
import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    // Processar o callback de autenticação
    const handleAuthCallback = async () => {
      try {
        console.log("Auth callback started, processing authentication");
        console.log("Current location:", location.pathname);
        console.log("Location hash:", location.hash);
        console.log("Location search:", location.search);
        
        // Verificar se há hash na URL (tipicamente vindo de OAuth)
        const hashParams = new URLSearchParams(location.hash.substring(1));
        const accessToken = hashParams.get("access_token");
        const refreshToken = hashParams.get("refresh_token");
        
        // Verificar se há parâmetros na query string (para alguns provedores OAuth)
        const queryParams = new URLSearchParams(location.search);
        const code = queryParams.get("code");
        
        console.log("Access token present:", !!accessToken);
        console.log("Code present:", !!code);
        
        if (accessToken) {
          console.log("OAuth tokens found in URL, setting session");
          // Se tiver tokens na URL, vamos usar para estabelecer a sessão
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || "",
          });
          
          if (error) {
            console.error("Error setting session:", error);
            throw error;
          }
          
          if (data.session) {
            console.log("Session established with OAuth tokens, redirecting to home");
            return navigate("/", { replace: true });
          }
        }
        
        // Caso tenha código de autorização mas não tokens diretos
        if (code) {
          console.log("Authorization code found in URL, exchanging for session");
          // O Supabase JS client irá processar esse código automaticamente
          // na maioria dos casos via getSession ou onAuthStateChange
        }
        
        // Verificar o estado atual da autenticação
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Erro ao verificar sessão:", error);
          toast({
            title: "Erro de autenticação",
            description: "Houve um problema ao processar a autenticação.",
            variant: "destructive",
          });
          return navigate("/auth", { replace: true });
        }
        
        if (data.session) {
          console.log("Sessão encontrada, redirecionando para home");
          return navigate("/", { replace: true });
        } else {
          console.log("Sessão não encontrada, redirecionando para login");
          return navigate("/auth", { replace: true });
        }
      } catch (error) {
        console.error("Erro no callback de autenticação:", error);
        toast({
          title: "Erro de autenticação",
          description: "Houve um problema ao processar a autenticação.",
          variant: "destructive",
        });
        navigate("/auth", { replace: true });
      }
    };
    
    // Executar ao carregar o componente
    handleAuthCallback();
    
    // Configurar o listener de estado de autenticação
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("Evento de autenticação:", event);
      
      if (event === "SIGNED_IN" && session) {
        navigate("/", { replace: true });
      } else if (event === "SIGNED_OUT") {
        navigate("/auth", { replace: true });
      }
    });

    return () => {
      if (authListener?.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, [navigate, toast, location]);

  return (
    <div className="flex min-h-screen items-center justify-center" style={{backgroundColor: "#d4f1f4"}}>
      <div className="text-center">
        <Loader2 className="h-12 w-12 mx-auto animate-spin text-[#05445e]" />
        <h2 className="mt-4 text-xl font-semibold text-[#05445e]">Processando autenticação...</h2>
        <p className="mt-2 text-[#189ab4]">Aguarde enquanto finalizamos o processo de login.</p>
      </div>
    </div>
  );
}
